import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getAnnouncementById, approveAnnouncement, denyAnnouncement } from '../api/apiClient';
import './AnnouncementApprovalModal.css';

const formatDate = (raw) => {
  if (!raw) return '';
  const utc = raw.includes('T') ? raw : raw.replace(' ', 'T') + 'Z';
  return new Date(utc).toLocaleString();
};

const EYEBROW = { pending: 'Approval Needed', denied: 'Announcement Denied', approved: 'Announcement Approved' };

const AnnouncementApprovalModal = ({ announcementId, currentUser, onClose }) => {
  const [ann, setAnn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denying, setDenying] = useState(false);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = () => {
    getAnnouncementById(announcementId)
      .then(res => setAnn(res.data))
      .catch(() => setError('Could not load this announcement.'))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [announcementId]);

  useEffect(() => {
    const down = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, [onClose]);

  const handleApprove = async () => {
    setSubmitting(true);
    setError('');
    try {
      await approveAnnouncement(announcementId, currentUser.id);
      load();
    } catch {
      setError('Failed to approve. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeny = async () => {
    setSubmitting(true);
    setError('');
    try {
      await denyAnnouncement(announcementId, currentUser.id, note.trim());
      load();
    } catch {
      setError('Failed to deny. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const viewLive = () => {
    onClose();
    navigate('/announcements', { state: { focusAnnouncementId: announcementId } });
  };

  return ReactDOM.createPortal(
    <div className="aam-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="aam-modal">
        <button className="aam-close" onClick={onClose}>✕</button>

        {loading ? (
          <p className="aam-loading">Loading…</p>
        ) : !ann ? (
          <p className="aam-loading">{error || 'Announcement not found.'}</p>
        ) : (
          <>
            <p className="aam-eyebrow">{EYEBROW[ann.status] || 'Announcement'}</p>
            <h2 className="aam-title">{ann.title}</h2>
            <p className="aam-meta">Posted by {ann.author_name} · {formatDate(ann.created_at)}</p>
            <div className="aam-content">{ann.content}</div>

            {ann.attachments?.length > 0 && (
              <div className="aam-attachments">
                {ann.attachments.map(a => (
                  <div key={a.id} className="aam-attachment">📎 {a.original_name}</div>
                ))}
              </div>
            )}

            {ann.status === 'pending' && currentUser?.isAdmin && (
              <div className="aam-actions">
                {!denying ? (
                  <>
                    <button className="aam-btn aam-btn--approve" disabled={submitting} onClick={handleApprove}>
                      {submitting ? 'Approving…' : 'Approve'}
                    </button>
                    <button className="aam-btn aam-btn--deny" disabled={submitting} onClick={() => setDenying(true)}>
                      Deny
                    </button>
                  </>
                ) : (
                  <div className="aam-deny-form">
                    <label className="aam-note-label">Note (optional) — explain why this was denied</label>
                    <textarea
                      className="aam-note-input"
                      rows={3}
                      value={note}
                      onChange={e => setNote(e.target.value)}
                      placeholder="e.g. Please run this by HR first…"
                      autoFocus
                    />
                    <div className="aam-deny-form-btns">
                      <button className="aam-btn aam-btn--deny-confirm" disabled={submitting} onClick={handleDeny}>
                        {submitting ? 'Denying…' : 'Confirm Deny'}
                      </button>
                      <button className="aam-btn aam-btn--cancel" disabled={submitting} onClick={() => { setDenying(false); setNote(''); }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {ann.status === 'pending' && !currentUser?.isAdmin && (
              <p className="aam-status-note">This announcement is awaiting admin approval.</p>
            )}

            {ann.status === 'denied' && (
              <div className="aam-decision-box aam-decision-box--denied">
                <p className="aam-decision-by">Denied by {ann.reviewer_name || 'an admin'}</p>
                <p className="aam-decision-note">{ann.denial_note ? ann.denial_note : <em>No reason was given.</em>}</p>
              </div>
            )}

            {ann.status === 'approved' && (
              <div className="aam-decision-box aam-decision-box--approved">
                <p className="aam-decision-by">Approved by {ann.reviewer_name || 'an admin'} — now live on the intranet</p>
                <button className="aam-btn aam-btn--approve" onClick={viewLive}>View announcement →</button>
              </div>
            )}

            {error && <p className="aam-error">{error}</p>}
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementApprovalModal;
